<template>
  <VOnboardingWrapper ref="wrapper" :steps="steps">
    <template #default="{ previous, next, step, exit, isFirst, isLast, index }">
      <VOnboardingStep
        class="VOnboardingStep-wrapper"
        :step="index"
        :class="{ 'VOnboardingStep-center': step?.center }"
      >
        <div class="card shadow-lg secondary-300 m-auto">
          <div class="card-body" :class="({ 'text-center': index == 0 }, step.class)">
            <h5 class="card-title" v-if="step.content.title">{{ step.content.title }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="exit"></button>
            <figure v-if="step.content.image || step.content.imageComponent" class="img-fluid px-4 mt-3 mb-4">
              <component v-if="step.content.imageComponent" :is="step.content.imageComponent" class="card-image" alt="Welcome image" />
              <img v-else class="card-image" :src="step.content.image" alt="Welcome image" />
            </figure>
            <p class="card-text" v-if="step.content.description">{{ step.content.description }}</p>
          </div>
          <div class="card-footer navigation">
            <template v-if="isFirst">
              <button @click="next" type="button" class="btn btn-primary mx-auto">
                {{ step.content.cta }}
              </button>
            </template>
            <template v-else>
              <button @click="previous" type="button" class="btn btn-primary">Back</button>
              <button @click="next" type="button" class="btn btn-primary">
                {{ isLast ? "Finish" : "Continue" }}
              </button>
              <button v-if="step.content.error" disabled type="button" class="btn btn-primary">
                <i class="bi bi-exclamation-triangle-fill me-1 opacity-75"></i>
                {{ step.content.error }}
              </button>
            </template>
          </div>
        </div>
      </VOnboardingStep>
    </template>
  </VOnboardingWrapper>
</template>

<script>
import { defineComponent, ref } from "vue";
import TourListIcon from "@/assets/icons/tour-list.svg";

import { VOnboardingWrapper, VOnboardingStep, useVOnboarding } from "v-onboarding";

export default defineComponent({
  components: {
    VOnboardingWrapper,
    VOnboardingStep
  },
  setup() {
    const wrapper = ref(null);
    const { start, goToStep, finish } = useVOnboarding(wrapper);
    let steps = [];
    const agendaSteps = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Welcome to PTC Wizard!",
          description:
            "It only takes about 30 seconds to learn what makes us the best parent-teacher conference app ever.",
          imageComponent: TourListIcon,
          cta: "Let's Begin"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "View your schedule the way you want",
          description:
            "When you sign in, you will see a page that lists your already-scheduled meetings. Choose between viewing all your scheduled meetings in a single list, or broken down by conference."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Let's schedule a meeting",
          description:
            "Simply find the conference you want and press here to start choosing a teacher and time to meet."
        }
      }
    ];

    // eslint-disable-next-line
    const matrixDetailedSteps = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Sign up for meetings",
          description: "With PTC Wizard, booking meetings is as easy as 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Let's Do It"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Step 1: Choose the date",
          description: "All dates of the conference are shown here. Simply press the one you want."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Step 2: Choose your teachers",
          description:
            "Press the TEACHERS button to see all available teachers and/or teams for that conference day. Press on any/all teachers to display their schedule.",
          error: "Choose a teacher"
        }
      },
      {
        attachTo: { element: "#matrix" },
        content: {
          title: "Step 3: Pick a timeslot",
          description:
            "This is where the magic happens. PTC Wizard shows you all your selected teachers' schedules together, and all you need to do is choose timeslots that work for you."
        },
        class: "primary-50",
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true
      },
      {
        attachTo: { element: ".btn-timeslot-available" },
        content: {
          title: "Choose Time",
          description:
            "Simply press on any time shown and your reservation is immediately saved. You can always change or cancel your meeting reservation. And to prevent double-booking, we update the available timeslots in real time."
        }
      },
      {
        attachTo: { element: "#collapse" },
        content: {
          title: "Fit more schedules on the screen",
          description:
            "Collapse or expand the width of columns in the table in order to see even more schedules at once."
        }
      },
      {
        attachTo: { element: ".handle" },
        content: {
          title: "Reorder teachers",
          description: "Drag columns left or right to make it easier to plan your schedule."
        }
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Review your agenda",
          description:
            "Your meeting is booked as soon as you choose a timeslot, but you can review all your meetings at any time by opening the agenda drawer. From here you can also choose to send a copy of your full agenda to your email address."
        }
      }
    ];
    const matrixSteps = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Sign up for meetings",
          description: "With PTC Wizard, booking meetings is as easy as 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Let's Do It"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Step 1: Choose the date",
          description: "All dates of the conference are shown here. Simply press the one you want."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Step 2: Choose your teachers",
          description:
            "Press the TEACHERS button to see all available teachers and/or teams for that conference day. Press on any/all teachers to display their schedule.",
          error: "Choose a teacher"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "Step 3: Pick a timeslot",
          image: "/tour-matrix-4.png",
          description:
            "This is where the magic happens. PTC Wizard shows you all your selected teachers' schedules together, and all you need to do is choose timeslots that work for you."
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Review your agenda",
          image: "/tour-matrix-5.png",
          description:
            "Your meeting is booked as soon as you choose a timeslot, but you can review all your meetings at any time by opening the agenda drawer. From here you can also choose to send a copy of your full agenda to your email address by pressing the Confirm Schedule button."
        }
      }
    ];

    return {
      wrapper,
      steps,
      agendaSteps,
      matrixSteps,
      start,
      finish,
      goToStep
    };
  }
});
</script>
